import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { GridDots } from '../components/GridDots'

export const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isMagicLinkSent, setIsMagicLinkSent] = useState(false)
  const [authMethod, setAuthMethod] = useState('password') // 'password' or 'magic-link'
  const navigate = useNavigate()
  const { signInWithPassword, signInWithMagicLink } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setError('')
      setLoading(true)
      
      if (authMethod === 'password') {
        await signInWithPassword({ email, password })
        navigate('/dashboard')
      } else {
        await signInWithMagicLink({ email })
        setIsMagicLinkSent(true)
      }
    } catch (error) {
      setError(error.message)
      console.error('Authentication error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (isMagicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E1E2E] relative">
        <GridDots />
        <div className="max-w-md w-full space-y-8 p-8 bg-[#2A2A3C] rounded-lg relative z-10">
          <div>
            <h2 className="text-center text-3xl font-bold text-white">Check Your Email</h2>
            <p className="mt-2 text-center text-sm text-gray-400">
              We've sent a magic link to {email}. Click the link to sign in.
            </p>
          </div>
          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setIsMagicLinkSent(false)
                setError('')
              }}
              className="text-[#6C63FF] hover:text-[#4A45B2]"
            >
              Try another email
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1E1E2E] relative">
      <GridDots />
      <div className="max-w-md w-full space-y-8 p-8 bg-[#2A2A3C] rounded-lg relative z-10">
        <div>
          <h2 className="text-center text-3xl font-bold text-white">Welcome Back</h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Sign in to your account
          </p>
        </div>
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-700 bg-[#1E1E2E] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C63FF] focus:border-transparent"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {authMethod === 'password' && (
              <div>
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-700 bg-[#1E1E2E] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C63FF] focus:border-transparent"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setAuthMethod(authMethod === 'password' ? 'magic-link' : 'password')}
                className="text-sm text-[#6C63FF] hover:text-[#4A45B2]"
              >
                {authMethod === 'password' ? 'Use magic link instead' : 'Use password instead'}
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#6C63FF] hover:bg-[#4A45B2] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6C63FF] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Signing in...</span>
                </div>
              ) : (
                authMethod === 'password' ? 'Sign in' : 'Send magic link'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
} 